import {
  createBrowserRouter,
  RouterProvider,
  Outlet,
  Navigate,
  useRouteError,
} from "react-router-dom";
import { QueryClient, QueryClientProvider } from "react-query";
import { Toaster } from "react-hot-toast";
import { AuthProvider } from "./contexts/AuthContext";
import { AppProvider } from "./contexts/AppContext";
import { ThemeProvider } from "./contexts/ThemeContext";
import ProtectedRoute from "./components/ProtectedRoute";
import ErrorBoundary from "./components/ErrorBoundary";
import Layout from "./components/Layout";
import Login from "./pages/Login";
import Signup from "./pages/Signup";
import ForgotPassword from "./pages/ForgotPassword";
import ProfileCompletion from "./pages/ProfileCompletion";
import Profile from "./pages/Profile";
import Reports from "./pages/Reports";
import Pricing from "./pages/Pricing";
import Home from "./pages/Home";
import RTTP from "./pages/RTTP";
import Experts from "./pages/Experts";
import Investors from "./pages/Investors";
import Technologies from "./pages/Technologies";
import Prototype from "./pages/Prototype";
import Admin from "./pages/Admin";
import Contact from "./pages/Contact";
import PressReleases from "./pages/PressReleases";
import Privacy from "./pages/Privacy";
import About from "./pages/About";
import Terms from "./pages/Terms";
import PricingPolicy from "./pages/PricingPolicy";
import RefundPolicy from "./pages/RefundPolicy";
import Careers from "./pages/Careers";
import Blog from "./pages/Blog";
import BlogPost from "./pages/BlogPost";
import QuickInquiryWidget from "./components/QuickInquiryWidget";
import "./index.css";

function RootLayout() {
  return (
    <>
      <Outlet />
      <QuickInquiryWidget />
    </>
  );
}

function RouterErrorElement() {
  const error = useRouteError();
  if (error?.status === 404) {
    return <Navigate to="/" replace />;
  }
  return (
    <div className="min-h-screen flex items-center justify-center bg-neutral-50">
      <div className="max-w-md w-full bg-white rounded-lg shadow-lg p-6 text-center">
        <h1 className="text-xl font-semibold text-neutral-900 mb-2">Something went wrong</h1>
        <p className="text-neutral-600 mb-6">
          We're sorry, but something unexpected happened.
        </p>
        <a href="/" className="btn-primary inline-block">Go Home</a>
      </div>
    </div>
  );
}

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      staleTime: 5 * 60 * 1000, // 5 minutes
      refetchOnWindowFocus: false,
    },
  },
});

const router = createBrowserRouter(
  [
    {
      element: <RootLayout />,
      errorElement: <RouterErrorElement />,
      children: [
        { path: "/", element: <Home /> },
        { path: "/login", element: <Login /> },
        { path: "/signup", element: <Signup /> },
        { path: "/forgot-password", element: <ForgotPassword /> },
        { path: "/profile-completion", element: <ProfileCompletion /> },
        { path: "/rttp", element: <RTTP /> },
        { path: "/experts", element: <Experts /> },
        { path: "/investors", element: <Investors /> },
        { path: "/technologies", element: <Technologies /> },
        { path: "/prototype", element: <Prototype /> },
        { path: "/contact", element: <Contact /> },
        { path: "/about", element: <About /> },
        { path: "/pricing", element: <Pricing /> },
        { path: "/admin", element: <Navigate to="/admin/dashboard" replace /> },
        { path: "/admin/:tabId", element: <Admin /> },
        { path: "/careers", element: <Careers /> },
        { path: "/blog", element: <Blog /> },
        { path: "/blog/:slug", element: <BlogPost /> },
        { path: "/press-releases", element: <PressReleases /> },
        { path: "/press-releases/:slug", element: <BlogPost /> },
        { path: "/privacy", element: <Privacy /> },
        { path: "/terms", element: <Terms /> },
        { path: "/pricing-policy", element: <PricingPolicy /> },
        { path: "/refund-policy", element: <RefundPolicy /> },
        { path: "*", element: <Navigate to="/" replace /> },
        {
          path: "/profile",
          element: (
            <ProtectedRoute>
              <Layout>
                <Profile />
              </Layout>
            </ProtectedRoute>
          ),
        },
        {
          path: "/reports",
          element: (
            <ProtectedRoute>
              <Layout>
                <Reports />
              </Layout>
            </ProtectedRoute>
          ),
        },
      ],
    },
  ],
  {
    future: {
      // opt-in to future flags
      v7_startTransition: true,
      v7_relativeSplatPath: true,
    },
  }
);

function App() {
  return (
    <ErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <ThemeProvider>
          <AuthProvider>
            <AppProvider>
              <div className="min-h-screen bg-white dark:bg-slate-950 transition-colors duration-300">
                <RouterProvider router={router} />
                <Toaster
                  position="top-right"
                  toastOptions={{
                    className: "bg-white dark:bg-slate-800 dark:text-slate-100 shadow-lg border dark:border-slate-700",
                    duration: 4000,
                  }}
                />
              </div>
            </AppProvider>
          </AuthProvider>
        </ThemeProvider>
      </QueryClientProvider>
    </ErrorBoundary>
  );
}

export default App;
